<template>
  <v-card flat tile style="background: transparent">
    <v-col>
      <v-row justify="space-between" align="center" class="ma-0">
        <span class="body-regular-smaller questionText mb-2"
          >{{ $t('message.quiz.QUESTION') }} {{ localQuestion.index + 1 }}</span
        >
        <v-btn
          v-if="questions.length > 1"
          icon
          x-small
          dense
          @click="onDeleteQuestion"
        >
          <v-icon color="feedback-danger">panda-remove</v-icon>
        </v-btn>
      </v-row>
      <v-textarea
        class="mt-7"
        v-model="localQuestion.statement.value"
        :placeholder="$t('message.quiz.insertQuestion')"
        :label="$t('message.quiz.question')"
        :rules="[rules.required, rules.maxLength120]"
        :counter="isQuestionFocused ? 120 : false"
        rows="1"
        style="font-size: 13px; padding-right: 0"
        outlined
        dense
        hide-details="auto"
        @focus="isQuestionFocused = true"
        @blur="isQuestionFocused = false"
      >
      </v-textarea>
      <v-divider class="my-6"></v-divider>
      <div class="question-answers mt-3">
        <v-row
          v-for="(answer, answerIndex) in localQuestion.answers"
          :key="answerIndex"
          class="ma-0 pa-0 mt-4 pb-4"
        >
          <v-col class="ma-0 pa-0 pl-2" cols="11">
            <span class="d-flex align-center">
              <span class="answer-index mb-4 mr-3">{{ answerIndex + 1 }}</span>
              <v-input
                :label="`${$t('message.quiz.alternative')} ${answerIndex + 1}`"
                v-model="answer.text"
                style="font-size: 13px; height: 45px"
                :rules="[rules.required, rules.maxLength30]"
                :counter="focusedAnswerIndex === answerIndex ? 30 : false"
                dense
                class="mt-3"
                outlined
                hide-details="auto"
                :placeholder="$t('message.quiz.answer')"
                @focus="focusedAnswerIndex = answerIndex"
                @blur="focusedAnswerIndex = -1"
              ></v-input>
            </span>
            <span>
              <span class="action-label">{{
                $t('message.pages.live.action')
              }}</span>
              <v-select
                v-model="answer.action"
                style="font-size: 13px; padding-left: 35px"
                class="mt-3 base-select"
                :items="availableAnswerTypes"
                item-text="name"
                item-value="value"
                outlined
                dense
                hide-details
                @change="handleActionChange(answer)"
              >
                <template v-slot:item="{ item }">
                  <v-tooltip bottom v-if="item.disabled">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        style="position: relative"
                        v-bind="attrs"
                        v-on="on"
                        class="disabled-item"
                      >
                        {{ item.name }}
                      </div>
                    </template>
                    <span>{{ item.tooltip }}</span>
                  </v-tooltip>
                  <div v-else style="position: relative">{{ item.name }}</div>
                </template>
              </v-select>
              <v-select
                v-if="answer.action === 'redirect_question'"
                v-model="answer.nextQuestion"
                style="font-size: 13px; padding-left: 35px"
                class="mt-3"
                :items="questionsSelect"
                :placeholder="$t('message.quiz.selectQuestion')"
                item-text="name"
                item-value="value"
                outlined
                dense
                hide-details
              />
              <v-input
                v-if="answer.action === 'open_link'"
                :label="$t('message.quiz.externalLink')"
                v-model="answer.link"
                style="font-size: 13px; padding-left: 35px"
                class="mt-8"
                :rules="[rules.required, rules.validUrl]"
                placeholder="http://"
                hide-details="auto"
                validate-on-blur
              ></v-input>
            </span>
            <div class="mt-3 ml-9" v-if="answer.action === 'redirect_video'">
              <template v-if="uploadingAnswers[answerIndex]">
                <div class="video-loading-container">
                  <base-progress-circular />
                </div>
              </template>
              <base-btn
                v-else-if="!isVideoSelected(answerIndex)"
                type="secondary"
                @click="selectedLibrary(answer, answerIndex)"
                >{{ $t('message.pages.video.buttonsPage.select') }}</base-btn
              >
              <div v-else class="d-flex align-center justify-space-between">
                <div
                  class="d-flex align-center video-clickable"
                  style="gap: 16px; cursor: pointer"
                  @click="openVideo(answerIndex)"
                >
                  <img
                    :src="
                      getVideoByIndex(answerIndex).status === 'CONVERTING' ||
                      getVideoByIndex(answerIndex).status === 'DRAFT'
                        ? require('@/assets/empty-preview.svg')
                        : getVideoByIndex(answerIndex).preview
                    "
                    style="height: 32px; width: 48px; border-radius: 4px"
                  />
                  <span
                    class="regular-bold-smaller neutral-30-base--text video-title-truncated"
                  >
                    {{ getVideoByIndex(answerIndex).title }}
                  </span>
                  <base-progress-circular
                    v-if="
                      getVideoByIndex(answerIndex).status === 'CONVERTING' ||
                      getVideoByIndex(answerIndex).status === 'DRAFT'
                    "
                  />
                </div>
                <base-btn
                  class="panda-icons --thumbnail edit-thumb-btn d-flex align-center"
                  type="neutral"
                  @click="selectedLibrary(answer, answerIndex)"
                  ><v-icon
                    style="
                      font-size: 16px;
                      color: var(--neutral-40-light-light) !important;
                    "
                    >panda-edit</v-icon
                  ></base-btn
                >
              </div>
            </div>
          </v-col>
          <v-col class="ma-0 pa-0 pl-2 d-flex justify-end" cols="1">
            <v-btn
              v-if="localQuestion.answers.length > 1"
              icon
              x-small
              class="mt-5"
              @click="deleteAnswer(answerIndex)"
              style="cursor: pointer"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="mb-7">
          <span
            @click="localQuestion.answers.length < 3 && newAnswer()"
            style="font-weight: 500"
            class="new-answer-btn d-flex align-center"
            :class="{
              'new-answer-btn--disabled': localQuestion.answers.length >= 3,
            }"
            :disabled="localQuestion.answers.length >= 3"
          >
            <v-icon
              class="new-answer-btn"
              size="15px"
              :color="
                localQuestion.answers.length >= 3
                  ? 'neutral-60'
                  : 'brand-60-base'
              "
              >mdi-plus</v-icon
            >
            <span class="new-answer-btn__text">
              {{ $t('message.quiz.alternative') }}
            </span>
          </span>
        </v-row>
      </div>
    </v-col>
  </v-card>
</template>
<script>
import _ from 'lodash'
import BaseProgressCircular from '@/components/BaseComponents/BaseProgressCircular.vue'
export default {
  name: 'QuestionComponent',
  components: { BaseProgressCircular },
  props: {
    question: {
      type: Object,
      default: () => ({
        statement: {
          value: '',
        },
        answers: [],
        index: 0,
      }),
    },
    questions: {
      type: Array,
      default: () => [],
    },
    onDeleteQuestion: {
      type: Function,
      default: () => {},
    },
    onChangeQuestion: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      availableAnswerTypes: [],
      localQuestion: {
        statement: {
          value: '',
        },
        answers: [],
        index: 0,
      },
      allVideos: [],
      videoTitles: {},
      answersSelect: null,
      videosByIndex: {},
      isValid: true,
      focusedAnswerIndex: -1,
      isQuestionFocused: false,
      uploadingAnswers: {},
      instanceId: '',
    }
  },
  async updated() {
    this.$root.$off('video-upload-started')
    this.$root.$off('video-upload-completed')
    this.$root.$off('manual-drop-interactive')
    this.$root.$on('manual-drop-interactive', (data) => {
      if (data && data.instanceId === this.instanceId) {
        this.handleManualDrop(data.selectedItem)
      }
    })
    this.$root.$on('video-upload-started', () => {
      if (this.answersSelect) {
        this.$set(this.uploadingAnswers, this.answerIndex, true)
        this.$delete(this.videosByIndex, this.answerIndex)
      }
    })

    this.$root.$on('video-upload-completed', async (videoId) => {
      try {
        const videoFound = await window.db.videos
          .where({ id: videoId })
          .toArray()
        if (videoFound.length > 0) {
          this.$set(this.videosByIndex, this.answerIndex, videoFound[0])
          this.$delete(this.uploadingAnswers, this.answerIndex)
        } else {
          this.$set(this.uploadingAnswers, this.answerIndex, true)
        }
      } catch (error) {
        console.error('Erro ao carregar vídeo:', error)
        this.$delete(this.uploadingAnswers, this.answerIndex)
      }
    })
  },
  async mounted() {
    this.$root.$on('changeLanguage', this.updateText)
    this.availableAnswerTypes = [
      {
        name: this.$i18next.t('message.quiz.noAction'),
        value: 'no_action',
      },
      {
        name: this.$i18next.t('message.quiz.nextQuestion'),
        value: 'redirect_question',
        disabled: !this.hasAvailableQuestions,
        tooltip: this.$i18next.t('message.quiz.noQuestionsAvailable'),
      },
      {
        name: this.$i18next.t('message.quiz.playAnotherVideo'),
        value: 'redirect_video',
      },
      {
        name: this.$i18next.t('message.quiz.openLink'),
        value: 'open_link',
      },
    ]

    this.instanceId = `question-${this.localQuestion.index}-${Date.now()}`

    await this.getVideos()

    this.$nextTick(() => {
      this.validateQuestion()
    })
  },
  beforeDestroy() {
    this.$root.$off('manual-drop-interactive')
    this.$root.$off('changeLanguage')
    this.$root.$off('video-upload-started')
    this.$root.$off('video-upload-completed')
  },
  computed: {
    questionsSelect() {
      return this.questions
        .map((question, index) => ({
          name: `${this.$i18next.t('message.quiz.questions')} ${index + 1}`,
          value: index,
        }))
        .filter((question, index) => index !== this.localQuestion.index)
    },
    hasAvailableQuestions() {
      return this.questionsSelect.length > 0
    },
    rules() {
      return {
        required: (v) => (v && !!v) || this.$t('message.requiredFill'),
        maxLength120: (v) =>
          (v && v.length <= 120) || this.$t('message.maximumLengthQuiz'),
        maxLength30: (v) =>
          (v && v.length <= 30) || this.$t('message.maximumLengthQuiz30'),
        validUrl: (v) => {
          if (!v) return true
          try {
            // Usar o construtor URL para validação mais robusta
            const url = new URL(v)
            // Verificar se tem protocolo http ou https
            return url.protocol === 'http:' || url.protocol === 'https:'
          } catch (error) {
            return this.$t('message.invalidUrl')
          }
        },
      }
    },
    isVideoSelected() {
      return (answerIndex) => {
        const answer = this.localQuestion.answers[answerIndex]
        return (
          answer && answer.redirectVideoId && this.getVideoByIndex(answerIndex)
        )
      }
    },
  },

  methods: {
    updateText() {
      this.availableAnswerTypes = [
        {
          name: this.$i18next.t('message.quiz.noAction'),
          value: 'no_action',
        },
        {
          name: this.$i18next.t('message.quiz.nextQuestion'),
          value: 'redirect_question',
          disabled: !this.hasAvailableQuestions,
          tooltip: this.$i18next.t('message.quiz.noQuestionsAvailable'),
        },
        {
          name: this.$i18next.t('message.quiz.playAnotherVideo'),
          value: 'redirect_video',
        },
        {
          name: this.$i18next.t('message.quiz.openLink'),
          value: 'open_link',
        },
      ]
    },
    async getVideos() {
      for (
        let answerIndex = 0;
        answerIndex < this.localQuestion.answers.length;
        answerIndex++
      ) {
        const answer = this.localQuestion.answers[answerIndex]
        if (answer.redirectVideoId) {
          try {
            const videoFound = await window.db.videos
              .where({ id: answer.redirectVideoId })
              .toArray()
            if (videoFound.length > 0) {
              this.$set(this.videosByIndex, answerIndex, videoFound[0])
              this.$delete(this.uploadingAnswers, answerIndex)

              this.$nextTick(() => {
                this.validateQuestion()
              })
            }
          } catch (error) {
            console.error('Erro ao carregar vídeo:', error)
            this.$delete(this.uploadingAnswers, answerIndex)
          }
        }
      }
    },
    newAnswer() {
      const answerIndex = this.localQuestion.answers.length
      let answerKey = 'answerA'

      if (answerIndex === 1) {
        answerKey = 'answerB'
      } else if (answerIndex === 2) {
        answerKey = 'answerC'
      }

      this.localQuestion.answers.push({
        text: this.$i18next.t(`message.quiz.${answerKey}`),
        action: 'no_action',
      })
    },
    async filterByExternalID(externalId) {
      if (!externalId) return

      const option = { video_external_id: externalId }

      const [videoInDB] = await window.db.videos.where(option).toArray()
      const videoInRoot = _.find(this.allVideos, option)

      let video

      if (videoInRoot) video = videoInRoot
      else {
        this.allVideos.push(videoInDB)
        video = videoInDB
      }

      if (video) {
        const videoData = {
          videoId: video.id,
          externalId: video.video_external_id,
          title: video.title,
        }

        this.$set(this.videoTitles, externalId, videoData)
      }
    },
    selectedLibrary(item, index) {
      this.answersSelect = item
      this.answerIndex = index
      this.$root.$emit('close-modal')
      this.$root.$emit('openAddVideoDestinyFunnel', {
        onSelect: (selectedItem) => {
          this.$root.$emit('manual-drop-interactive', {
            instanceId: this.instanceId,
            selectedItem: selectedItem,
          })
        },
      })
    },
    handleManualDrop(selectedItem) {
      this.localQuestion.answers[this.answerIndex].redirectVideoId =
        selectedItem.id
      this.localQuestion.answers[this.answerIndex].redirectVideoExternalId =
        selectedItem.video_external_id

      this.$set(this.videosByIndex, this.answerIndex, selectedItem)
      this.$delete(this.uploadingAnswers, this.answerIndex)

      this.videoSelected = selectedItem

      this.$emit('onChangeQuestion', this.localQuestion)

      this.$nextTick(() => {
        this.validateQuestion()
      })

      this.$forceUpdate()
    },
    getVideoByIndex(answerIndex) {
      return this.videosByIndex[answerIndex]
    },
    handleActionChange(answer) {
      if (answer.action === 'open_link') {
        answer.link = 'http://'
      }
      if (answer.action !== 'redirect_video') {
        const answerIndex = this.localQuestion.answers.indexOf(answer)
        if (answerIndex !== -1) {
          this.$delete(this.uploadingAnswers, answerIndex)
          this.$delete(this.videosByIndex, answerIndex)
        }
      }
    },
    updateQuestionStatement(e) {
      this.localQuestion.statement.value = e
    },
    deleteAnswer(index) {
      this.localQuestion.answers.splice(index, 1)
      if (this.videosByIndex[index]) {
        this.$delete(this.videosByIndex, index)
      }
      const newVideosByIndex = {}
      Object.keys(this.videosByIndex).forEach((key) => {
        const idx = parseInt(key)
        if (idx > index) {
          newVideosByIndex[idx - 1] = this.videosByIndex[idx]
        } else if (idx < index) {
          newVideosByIndex[idx] = this.videosByIndex[idx]
        }
      })
      this.videosByIndex = newVideosByIndex
    },
    validateQuestion() {
      const isStatementValid =
        this.rules.required(this.localQuestion.statement.value) === true &&
        this.rules.maxLength120(this.localQuestion.statement.value) === true

      const areAnswersValid = this.localQuestion.answers.every((answer) => {
        const isTextValid =
          this.rules.required(answer.text) === true &&
          this.rules.maxLength30(answer.text) === true

        let isActionValid = true
        if (answer.action === 'redirect_video') {
          isActionValid = !!answer.redirectVideoId
        } else if (answer.action === 'redirect_question') {
          isActionValid =
            answer.nextQuestion !== undefined && answer.nextQuestion !== null
        } else if (answer.action === 'open_link') {
          try {
            const url = new URL(answer.link)
            isActionValid =
              !!answer.link &&
              answer.link.trim() !== '' &&
              (url.protocol === 'http:' || url.protocol === 'https:')
          } catch (error) {
            isActionValid = false
          }
        }

        return isTextValid && isActionValid
      })

      this.isValid = isStatementValid && areAnswersValid
      this.$emit('validation-change', this.isValid)
      return this.isValid
    },
    openVideo(answerIndex) {
      const video = this.getVideoByIndex(answerIndex)
      if (!video || !video.id) {
        console.warn('Nenhum vídeo selecionado para abrir')
        return
      }

      try {
        const videoUrl = `${window.location.origin}/#/videos/${video.id}`

        const newWindow = window.open(videoUrl, '_blank')

        if (!newWindow) {
          console.warn(
            'Não foi possível abrir a nova aba. Verifique se o popup está bloqueado.'
          )
          window.location.href = videoUrl
        }
      } catch (error) {
        console.error('Erro ao abrir vídeo:', error)
        window.location.href = `${window.location.origin}/#/videos/${video.id}`
      }
    },
  },
  watch: {
    question: {
      immediate: true,
      handler(newValue) {
        this.localQuestion = { ...newValue }
        this.getVideos()
        this.localQuestion.answers.forEach((answer) => {
          if (answer.action === 'redirect_video') {
            this.filterByExternalID(answer.redirectVideoExternalId)
          }
        })
      },
    },
    localQuestion: {
      deep: true,
      handler(newValue) {
        this.$emit('update:question', { ...newValue })
        this.onChangeQuestion(newValue)
        this.getVideos()
        this.validateQuestion()
        newValue.answers.forEach((answer) => {
          if (answer.action === 'redirect_video') {
            this.filterByExternalID(answer.redirectVideoExternalId)
          }
        })
      },
    },
    locale: {
      handler() {
        this.updateText()
      },
      immediate: true,
    },
    hasAvailableQuestions: {
      immediate: true,
      handler(newValue) {
        const redirectQuestionOption = this.availableAnswerTypes.find(
          (type) => type.value === 'redirect_question'
        )
        if (redirectQuestionOption) {
          redirectQuestionOption.disabled = !newValue
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
.answer-index {
  width: 24px;
  height: 16px;
  background-color: varFigma(neutral-95-lightest);
  font-size: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .v-label {
  left: -10px !important;
}

.video-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 8px;
  background: varFigma(neutral-95-lightest);
  border-radius: 8px;
}

.new-answer-btn {
  color: varFigma(brand-60-base);
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &__text {
    &:hover {
      text-decoration: underline;
    }
  }

  &--disabled {
    color: varFigma(neutral-60);
    cursor: not-allowed;

    .new-answer-btn__text:hover {
      text-decoration: none;
    }
  }
}

.action-label {
  position: relative;
  top: 12px;
  left: 37px;
  font-size: 12px;
  color: var(--neutral-20-dark);
  font-weight: 500;
}

.disabled-item {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: all !important;
}

.questionText {
  color: varFigma(neutral-60);
}

::v-deep .v-select.base-select {
  .v-menu__content {
    .v-select-list {
      .v-list-item {
        &:hover {
          background: var(--brand-95-lighter-light) !important;
          &::before {
            opacity: 0 !important;
          }
        }

        &.v-list-item--disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: all !important;

          &:hover {
            background: var(--brand-95-lighter-light) !important;
          }
        }
      }
    }
  }
}

.video-title-truncated {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.video-clickable {
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}
</style>

<style lang="scss">
::v-deep .v-select.base-select {
  .v-menu__content {
    .v-select-list {
      .v-list-item {
        &:hover {
          background: var(--brand-95-lighter-light) !important;
          &::before {
            opacity: 0 !important;
          }
        }

        &.v-list-item--disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: all !important;

          &:hover {
            background: var(--brand-95-lighter-light) !important;
          }
        }
      }
    }
  }
}
</style>
